/**
 * Zentrale API-Fehlercodes → Enduser-Texte.
 * Backend: `error` = Code, optional `message` = spezifischer Text (hat Vorrang in extractErrorMessage).
 */

export const ERROR_MESSAGES = Object.freeze({
  UNAUTHORIZED: "Bitte melde dich erneut an.",
  FORBIDDEN: "Du hast keine Berechtigung für diese Aktion.",
  NOT_ASSIGNED: "Du bist diesem Eintrag nicht zugewiesen.",
  NOT_FOUND: "Der Eintrag wurde nicht gefunden oder ist nicht mehr verfügbar.",
  INVALID_INPUT: "Bitte prüfe deine Eingaben.",
  INVALID_ID: "Die angeforderte ID ist ungültig.",
  INVALID_STATUS: "Dieser Status ist ungültig.",
  TRANSITION_NOT_ALLOWED: "Dieser Statuswechsel ist aktuell nicht möglich.",
  CONFLICT: "Diese Aktion ist im Moment nicht möglich. Bitte aktualisiere die Seite.",
  MODULE_ACCESS_FORBIDDEN: "Du hast keinen Zugriff auf dieses Modul.",
  UNSUPPORTED_MEDIA_TYPE: "Dieser Dateityp wird nicht unterstützt.",
  PAYLOAD_TOO_LARGE: "Die Datei ist zu gross.",
  FILE_TOO_LARGE: "Die Datei ist zu gross.",
  INTERNAL_ERROR: "Es ist ein unerwarteter Fehler aufgetreten. Bitte versuche es erneut.",
  INVALID_CREDENTIALS: "Benutzername oder Passwort ist falsch.",
  INVALID_SIGNATURE_CODE: "Persönlicher Code ist falsch.",
  KONTROLL_INCOMPLETE: "Bitte zuerst Kontrollfirma und Bericht-Nr. speichern.",
  SIGNATURE_REQUIRED: "Bitte unterschreibe das Protokoll, bevor du es abschliesst.",
  TOO_OLD: "Dieser Eintrag liegt zu weit in der Vergangenheit und kann nicht mehr bearbeitet werden.",
  YEAR_CLOSED:
    "Dieses Datum liegt in einem abgeschlossenen Jahr. Bitte Korrekturbuchung verwenden.",
  YEAR_CLOSE_NOT_CONFIGURED:
    "Jahresabschluss ist noch nicht eingerichtet. Bitte Migration 039 ausführen.",
  DATE_IN_FUTURE_NOT_ALLOWED: "Einträge in der Zukunft sind nicht erlaubt.",
  MISSING_PROJEKTLEITER:
    "Bei „Rechnung durch Projektleiter“ muss ein Projektleiter ausgewählt sein.",
});

/** Legacy-Freitexte in `error` (kein SCREAMING_SNAKE) → Standardtext */
export const LEGACY_ERROR_TEXT_MAP = Object.freeze({
  "keine berechtigung": ERROR_MESSAGES.FORBIDDEN,
  "nicht erlaubt": ERROR_MESSAGES.FORBIDDEN,
  "nicht gefunden": ERROR_MESSAGES.NOT_FOUND,
  "nicht eingeloggt": ERROR_MESSAGES.UNAUTHORIZED,
  "ungültige id": ERROR_MESSAGES.INVALID_ID,
  "ungültiger typ": ERROR_MESSAGES.INVALID_INPUT,
});

export const HTTP_STATUS_MESSAGES = Object.freeze({
  401: ERROR_MESSAGES.UNAUTHORIZED,
  403: ERROR_MESSAGES.FORBIDDEN,
  404: ERROR_MESSAGES.NOT_FOUND,
  409: ERROR_MESSAGES.CONFLICT,
  422: ERROR_MESSAGES.INVALID_INPUT,
  500: ERROR_MESSAGES.INTERNAL_ERROR,
});

const SCREAMING_CODE_RE = /^[A-Z][A-Z0-9_]*$/;

export function isApiErrorCode(value) {
  const u = String(value ?? "").trim().toUpperCase();
  return !!u && SCREAMING_CODE_RE.test(u);
}

/**
 * @param {string} errorCode
 * @param {string} [fallback]
 * @returns {string}
 */
export function getErrorMessage(errorCode, fallback = "") {
  const key = String(errorCode ?? "").trim().toUpperCase();
  if (!key) return fallback;
  if (ERROR_MESSAGES[key]) return ERROR_MESSAGES[key];
  return fallback;
}

/**
 * @param {number} status
 * @param {string} [fallback]
 * @returns {string}
 */
export function getStatusFallbackMessage(status, fallback = "") {
  const n = Number(status);
  if (Number.isFinite(n) && HTTP_STATUS_MESSAGES[n]) return HTTP_STATUS_MESSAGES[n];
  return fallback;
}

/**
 * @param {string} rawErrorText – Legacy-Freitext aus data.error
 * @returns {string}
 */
export function mapLegacyErrorText(rawErrorText) {
  const normalized = String(rawErrorText ?? "").trim().toLowerCase();
  if (!normalized || isApiErrorCode(normalized)) return "";
  return LEGACY_ERROR_TEXT_MAP[normalized] || "";
}
